import { Field, Textarea } from '../../../components/ui/Field';
import { Button } from '../../../components/ui/Button';
import { Section } from '../../../components/ui/Panel';
import { SearchableSelect } from '../../../components/ui/SearchableSelect';

function countHeaders(text) {
  return String(text ?? '')
    .split(/\r?\n/)
    .filter((line) => /^[^\s:]+:/.test(line)).length;
}

/**
 * How the message is generated: the Inbox Pattern and any custom headers.
 *
 * Grouped apart from the campaign settings because these shape how the MIME message is
 * built rather than who it is from or to.
 */
export function MailHeadersCard({
  className,
  form,
  setField,
  inboxPatterns,
  isLoadingInboxPatterns,
  inboxPatternsError,
  onRetryInboxPatterns
}) {
  const headerCount = countHeaders(form.customHeaders);

  return (
    <Section
      title="Mail Header"
      className={className}
      icon="fa-heading"
      actions={
        headerCount > 0 ? (
          <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700">
            {headerCount} custom
          </span>
        ) : null
      }
    >
      <Field label="Inbox Pattern" htmlFor="inbox-pattern">
        <SearchableSelect
          id="inbox-pattern"
          value={form.inboxPatternId}
          options={[
            {
              id: '',
              name: 'Default (existing behavior)',
              description: 'Use the existing email generation behavior.'
            },
            ...inboxPatterns
          ]}
          onChange={(value) => setField('inboxPatternId', value)}
          disabled={isLoadingInboxPatterns || Boolean(inboxPatternsError)}
          placeholder={isLoadingInboxPatterns ? 'Loading patterns…' : 'Search Inbox Patterns'}
          aria-describedby={isLoadingInboxPatterns || inboxPatternsError ? 'inbox-pattern-status' : undefined}
        />
        {isLoadingInboxPatterns ? (
          <span id="inbox-pattern-status" className="flex items-center gap-1.5 text-xs text-muted" role="status">
            <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
            Loading Inbox Patterns…
          </span>
        ) : inboxPatternsError ? (
          <span id="inbox-pattern-status" className="flex flex-wrap items-center gap-2 text-xs text-danger-600" role="alert">
            Could not load Inbox Patterns. Sending uses Default.
            <Button variant="ghost" size="xs" onClick={onRetryInboxPatterns}>
              Retry
            </Button>
          </span>
        ) : null}
      </Field>

      <Field
        label="Custom Headers (optional)"
        htmlFor="custom-headers"
        // Grows to take the column's spare height, so the column ends level with the others.
        className="flex-1"
        hint={
          <>
            One per line as <code className="font-mono">Header-Name: value</code>. Overrides any
            generated header with the same name.
          </>
        }
      >
        <Textarea
          id="custom-headers"
          placeholder={'X-Campaign-ID: summer2026\nList-Unsubscribe: <mailto:unsubscribe@example.com>'}
          value={form.customHeaders}
          onChange={(event) => setField('customHeaders', event.target.value)}
          rows={7}
          spellCheck={false}
          className="min-h-[120px] flex-1 font-mono text-xs leading-snug"
        />
      </Field>
    </Section>
  );
}
